import React, { useState, useEffect } from 'react';
import {
    Search, Bell, CheckSquare, Users, TrendingUp, Clock, Plus, Bot,
    Upload, Send, CheckCircle2, AlertCircle, FileText, Menu, File,
    CheckCircleIcon, Sparkles, ArrowRight, GraduationCap, ListChecks,
    X, RefreshCw, Loader2, ArrowUpRight, Target, ClipboardList,
    MessageSquare, ChevronRight, Eye, Zap, ShieldCheck, ShieldAlert
} from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { useSurvey } from '../../context/SurveyContext';
import api from '../../utils/api';

// ═══════════════════════════════════════════════════════════
//  DESIGN TOKENS
// ═══════════════════════════════════════════════════════════
const T = {
    textPrimary: 'var(--foreground)',
    textSecondary: 'var(--muted-foreground)',
    textMuted: 'var(--muted-foreground)',
    accent: 'var(--accent)',
    accentHover: 'var(--accent-light)',
    accentBg: '#FEF2EE',
    border: 'var(--border)',
    borderLight: 'var(--border)',
    bgPage: 'var(--background)',
    bgCard: 'var(--card)',
    bgHover: 'var(--secondary)',
    warning: 'var(--warning)',
    warningBg: '#FEF3C7',
    warningText: '#92400E',
    success: '#10b981',
    successBg: '#ECFDF5',
    successText: '#065f46',
    danger: '#ef4444',
    dangerBg: '#FEF2F2',
    dangerText: '#991b1b',
    info: '#3b82f6',
    infoBg: '#EFF6FF',
    infoText: '#1e40af',
    purple: '#8b5cf6',
    purpleBg: '#F5F3FF',
    purpleText: '#6d28d9',
    cardBorder: '1px solid var(--border)',
    cardRadius: '8px',
    cardPadding: '20px',
    cardShadow: '0 1px 3px rgba(0,0,0,0.04)',
    cardShadowHover: '0 4px 16px rgba(0,0,0,0.08)',
    fontSm: '13px',
    fontBase: '14px',
    fontLg: '16px',
    fontXl: '20px',
    fontXxl: '24px',
    btnRadius: '8px',
    btnPadding: '8px 16px',
};

// ── Skeleton ──
const Skeleton = ({ width = '100%', height = '20px', borderRadius = T.cardRadius }) => (
    <div style={{
        width, height, borderRadius,
        background: `linear-gradient(90deg, ${T.bgHover} 25%, ${T.bgCard} 50%, ${T.bgHover} 75%)`,
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
    }} />
);

// ── Quick Action ──
const QuickAction = ({ icon: Icon, label, onClick, primary }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                height: '36px', padding: T.btnPadding, fontSize: T.fontBase,
                fontWeight: 600, borderRadius: T.btnRadius, cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: primary ? 'none' : T.cardBorder,
                background: primary ? (hovered ? T.accentHover : T.accent) : (hovered ? T.bgHover : T.bgCard),
                color: primary ? '#FFF' : T.textPrimary,
                boxShadow: primary ? '0 2px 8px rgba(232, 111, 74, 0.2)' : '0 1px 2px rgba(0,0,0,0.03)',
            }}
        >
            <Icon size={16} />
            {label}
        </button>
    );
};

// ── Lifecycle Card (Stat Card) ──
const LifecycleCard = ({ value, label, sublabel, icon: Icon, color, bgColor, onClick, pulse }) => {
    const [hov, setHov] = useState(false);
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                background: T.bgCard, borderTop: T.cardBorder, borderRight: T.cardBorder, borderBottom: T.cardBorder, borderRadius: T.cardRadius,
                padding: T.cardPadding, cursor: onClick ? 'pointer' : 'default', transition: 'all 0.25s ease',
                boxShadow: hov && onClick ? T.cardShadowHover : T.cardShadow,
                borderLeft: `4px solid ${color}`,
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                minHeight: '130px', position: 'relative',
                transform: hov && onClick ? 'translateY(-2px)' : 'none',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{
                    width: '42px', height: '42px', borderRadius: '10px',
                    background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon size={20} color={color} />
                </div>
                {pulse && (
                    <span style={{
                        width: '8px', height: '8px', borderRadius: '50%', background: color,
                        animation: 'pulse 2s infinite'
                    }} />
                )}
            </div>
            <div style={{ marginTop: '14px' }}>
                <div style={{
                    fontSize: '30px', fontWeight: 700, color: T.textPrimary, lineHeight: 1.1,
                    fontVariantNumeric: 'tabular-nums'
                }}>
                    {value}
                </div>
                <div style={{ fontSize: T.fontSm, color: T.textSecondary, fontWeight: 500, marginTop: '4px' }}>
                    {label}
                </div>
                {sublabel && (
                    <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '2px' }}>{sublabel}</div>
                )}
            </div>
        </div>
    );
};

// ── Insight Card (Adapted for Action Items) ──
const InsightCard = ({ icon, title, subtitle, meta, action, onClick, onDelete, isPrimary }) => {
    const [hov, setHov] = useState(false);
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '14px 16px',
                background: isPrimary ? (hov ? '#F5F3FF' : '#FAF5FF') : (hov ? '#EFF6FF' : '#F8FAFC'),
                border: `1px solid ${hov ? (isPrimary ? T.purple : T.info) : T.borderLight}`,
                borderRadius: T.cardRadius, cursor: 'pointer',
                transition: 'all 0.2s', marginBottom: '8px'
            }}
        >
            <div style={{
                width: '32px', height: '32px', borderRadius: '8px',
                background: isPrimary ? T.purpleBg : T.infoBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
            }}>
                {icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2px' }}>
                    <div style={{ fontSize: T.fontBase, color: T.textPrimary, fontWeight: 600, lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {title}
                    </div>
                    {onDelete && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            style={{
                                background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px',
                                display: 'flex', color: T.textMuted, marginLeft: '8px', opacity: hov ? 1 : 0, transition: 'opacity 0.2s'
                            }}
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {subtitle && (
                    <div style={{ fontSize: '12px', color: T.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                        {subtitle}
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                    {meta}
                </div>
            </div>
            {action && (
                <div style={{
                    fontSize: '12px', color: isPrimary ? T.purpleText : T.infoText, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: '4px', alignSelf: 'center', marginLeft: '8px'
                }}>
                    {action} <ChevronRight size={12} />
                </div>
            )}
        </div>
    );
};

// ── Activity Item ──
const ActivityItem = ({ icon: Icon, iconColor, iconBg, title, subtitle, time }) => (
    <div style={{
        display: 'flex', alignItems: 'start', gap: '12px', padding: '10px 0',
        borderBottom: `1px solid ${T.borderLight}`,
    }}>
        <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: iconBg, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, marginTop: '2px'
        }}>
            <Icon size={14} color={iconColor} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: T.fontBase, color: T.textPrimary, fontWeight: 500, lineHeight: '1.4' }}>
                {title}
            </div>
            <div style={{ fontSize: '12px', color: T.textSecondary, marginTop: '2px' }}>
                {subtitle}
            </div>
        </div>
        <div style={{ fontSize: '11px', color: time.includes('Due') || time.includes('Overdue') ? T.warningText : T.textMuted, whiteSpace: 'nowrap', marginTop: '4px' }}>
            {time}
        </div>
    </div>
);


const DashboardHomeEmployee = ({ sidebarCollapsed, onMenuClick, onAddEmployee }) => {
    const [employee, setEmployee] = useState(null);
    const [pendingSurveys, setPendingSurveys] = useState([]);
    const [stats, setStats] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingActivities, setLoadingActivities] = useState(true);
    const navigate = useNavigate();
    const { token } = useParams();
    const location = useLocation();
    const { recentAssignments, dismissNotification } = useNotifications();
    const { startSurvey } = useSurvey();

    const isPreboarding = location.pathname.includes('/preboard/');
    const basePath = isPreboarding ? `/preboard/${token}/dashboard` : '/employee';

    const fetchPendingSurveys = async () => {
        try {
            const response = await api.get('/surveys/employee/my-surveys');
            const surveys = response.data.surveys || [];
            const pending = surveys.filter(s => s.status === 'pending' || s.status === 'in_progress');
            setPendingSurveys(pending);
        } catch (error) {
            console.error("Error fetching surveys:", error);
        }
    };

    const fetchDashboardStats = async () => {
        try {
            const response = await api.get('/dashboard/employee/stats');
            setStats(response.data.stats);
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
        } finally {
            setLoadingStats(false);
        }
    };

    const fetchRecentActivity = async () => {
        try {
            const response = await api.get('/dashboard/employee/recent-activity', {
                params: { limit: 6 }
            });
            setActivities(response.data.activities || []);
        } catch (error) {
            console.error('Error fetching recent activity:', error);
        } finally {
            setLoadingActivities(false);
        }
    };

    const fetchEmployeeInfo = async () => {
        try {
            const response = await api.get('/auth/me');
            setEmployee(response.data.user);
        } catch (error) {
            console.error('Error fetching employee:', error);
        }
    };

    useEffect(() => {
        fetchEmployeeInfo();
        fetchPendingSurveys();
        fetchDashboardStats();
        fetchRecentActivity();

        const interval = setInterval(() => {
            fetchDashboardStats();
            fetchRecentActivity();
        }, 60000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getActivityIcon = (type) => {
        switch (type) {
            case 'task_completed':
                return { icon: CheckCircle2, color: T.successText, bg: T.successBg };
            case 'task_assigned':
                return { icon: FileText, color: T.infoText, bg: T.infoBg };
            case 'deadline_approaching':
                return { icon: Clock, color: T.warningText, bg: T.warningBg };
            case 'file_shared':
                return { icon: File, color: T.purpleText, bg: T.purpleBg };
            case 'survey_completed':
                return { icon: CheckSquare, color: T.purpleText, bg: T.purpleBg };
            case 'survey_assigned':
                return { icon: ClipboardList, color: T.purpleText, bg: T.purpleBg };
            case 'message_received':
                return { icon: MessageSquare, color: T.accent, bg: T.accentBg };
            default:
                return { icon: AlertCircle, color: T.textSecondary, bg: T.bgHover };
        }
    };

    const formatActivityTime = (date) => {
        if (!date) return '';
        const now = new Date();
        const activityDate = new Date(date);
        const diffMs = now - activityDate;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMs < 0) {
            const futureDays = Math.ceil(Math.abs(diffMs) / 86400000);
            if (futureDays === 0) return 'Due today';
            if (futureDays === 1) return 'Due tomorrow';
            return `Due in ${futureDays} days`;
        }

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return activityDate.toLocaleDateString();
    };

    const checkPerm = (perm) => {
        if (!employee || !employee.permissions) return true;
        const scope = employee.permissions[perm];
        return scope && scope !== 'none' && scope !== false;
    };

    const quickActions = [
        {
            title: 'View Tasks',
            icon: CheckCircleIcon,
            isPrimary: true,
            onClick: () => navigate(`${basePath}/tasks`),
            permission: 'task.view'
        },
        {
            title: 'Chat with AI',
            icon: Bot,
            isPrimary: false,
            onClick: () => navigate(`${basePath}/ai`),
            permission: 'ai.use_assistant'
        },
        {
            title: 'View Notices',
            icon: Bell,
            isPrimary: false,
            onClick: () => navigate(`${basePath}/notices`),
            permission: 'notice.view'
        },
        {
            title: 'View Documents',
            icon: File,
            isPrimary: false,
            onClick: () => navigate(`${basePath}/files`),
            permission: 'file.view'
        },
    ].filter(action => !action.permission || checkPerm(action.permission));

    const activityItems = activities.map(activity => {
        const iconInfo = getActivityIcon(activity.activity_type);
        return {
            icon: iconInfo.icon,
            iconColor: iconInfo.color,
            iconBg: iconInfo.bg,
            title: activity.title,
            subtitle: activity.subtitle || '',
            time: formatActivityTime(activity.activity_time)
        };
    });

    const displayActivities = activityItems.length > 0 ? activityItems : [
        {
            icon: Clock,
            iconColor: T.textSecondary,
            iconBg: T.bgHover,
            title: 'No recent activity',
            subtitle: 'Activity will appear here',
            time: ''
        }
    ];

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'quiz': return <FileText size={16} color={T.infoText} />;
            case 'training': return <GraduationCap size={16} color={T.infoText} />;
            case 'checklist': return <ListChecks size={16} color={T.infoText} />;
            default: return <CheckSquare size={16} color={T.infoText} />;
        }
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return null;
        const date = new Date(dueDate);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return { text: 'Overdue', color: T.dangerText, bg: T.dangerBg };
        if (diffDays === 0) return { text: 'Due Today', color: T.warningText, bg: T.warningBg };
        if (diffDays === 1) return { text: 'Due Tomorrow', color: T.warningText, bg: T.warningBg };
        if (diffDays <= 7) return { text: `Due in ${diffDays} days`, color: T.warningText, bg: T.warningBg };
        return { text: `Due ${date.toLocaleDateString()}`, color: T.textSecondary, bg: T.bgHover };
    };

    return (
        <>
            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
            `}</style>

            <main style={{ padding: '32px', maxWidth: '1280px', margin: '0 auto', color: T.textPrimary }}>

                {/* ── HEADER ── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
                    <div>
                        <h1 style={{ fontSize: T.fontXxl, fontWeight: 700, color: T.textPrimary, margin: 0 }}>
                            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {employee?.first_name || 'there'}
                        </h1>
                        <p style={{ fontSize: T.fontBase, color: T.textSecondary, marginTop: '4px' }}>
                            Here's what's happening with your tasks today.
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setLoadingStats(true); setLoadingActivities(true);
                            fetchDashboardStats(); fetchRecentActivity();
                        }}
                        style={{
                            background: T.bgCard, border: T.cardBorder, borderRadius: T.btnRadius,
                            padding: '8px', cursor: 'pointer', color: T.textSecondary,
                            boxShadow: T.cardShadow, transition: 'all 0.2s'
                        }}
                        title="Refresh data"
                    >
                        <RefreshCw size={18} className={loadingStats ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* ── QUICK ACTIONS ── */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
                    {quickActions.map((action, i) => (
                        <QuickAction
                            key={i}
                            icon={action.icon}
                            label={action.title}
                            onClick={action.onClick}
                            primary={action.isPrimary}
                        />
                    ))}
                </div>

                {/* ── SECTION 1: AT A GLANCE (Stats) ── */}
                <div style={{ marginBottom: '28px' }}>
                    <h2 style={{ fontSize: T.fontLg, fontWeight: 700, color: T.textPrimary, marginBottom: '14px' }}>
                        At a Glance
                    </h2>

                    {loadingStats ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius, padding: T.cardPadding }}>
                                    <Skeleton width="40px" height="40px" borderRadius="10px" />
                                    <div style={{ marginTop: '16px' }}>
                                        <Skeleton width="40%" height="28px" />
                                        <Skeleton width="60%" height="14px" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                            <LifecycleCard
                                value={stats ? `${stats.onboardingProgress.value}%` : '0%'}
                                label={(isPreboarding || employee?.lifecycle_stage === 'preboarding') ? 'Preboarding Progress' : 'Onboarding Progress'}
                                sublabel={stats ? `Completed ${stats.onboardingProgress.completed} of ${stats.onboardingProgress.total} tasks` : 'Loading...'}
                                icon={CheckSquare}
                                color={T.info}
                                bgColor={T.infoBg}
                                pulse={stats && stats.onboardingProgress.value > 0 && stats.onboardingProgress.value < 100}
                                onClick={() => navigate(`${basePath}/tasks`)}
                            />
                            <LifecycleCard
                                value={stats ? `${stats.complianceScore.value}%` : '0%'}
                                label="Compliance Score"
                                sublabel={stats ? (
                                    stats.complianceScore.status === 'no_activity' ? 'No activity yet' :
                                        stats.complianceScore.status === 'above_target' ? 'Above target' :
                                            stats.complianceScore.status === 'on_target' ? 'On target' : 'Below target'
                                ) : 'Loading...'}
                                icon={ShieldCheck}
                                color={stats?.complianceScore.status === 'no_activity' ? (T.textMuted || '#8A8F98') : stats?.complianceScore.status === 'above_target' ? T.success : stats?.complianceScore.status === 'on_target' ? T.infoText : T.dangerText}
                                bgColor={stats?.complianceScore.status === 'no_activity' ? (T.bgHover || '#F2EFED') : stats?.complianceScore.status === 'above_target' ? T.successBg : stats?.complianceScore.status === 'on_target' ? T.infoBg : T.dangerBg}
                                onClick={() => navigate(`${basePath}/surveys`)}
                            />
                            <LifecycleCard
                                value={stats ? `${stats.averageScore.value}%` : '0%'}
                                label="Average Score"
                                sublabel={stats ? (
                                    stats.averageScore.change >= 0
                                        ? `+${stats.averageScore.change}% from last month`
                                        : `${stats.averageScore.change}% from last month`
                                ) : 'Loading...'}
                                icon={Target}
                                color={stats?.averageScore.change >= 0 ? T.success : T.dangerText}
                                bgColor={stats?.averageScore.change >= 0 ? T.successBg : T.dangerBg}
                                onClick={() => navigate(`${basePath}/tasks`)}
                            />
                            <LifecycleCard
                                value={stats ? `${stats.completionRate.value}%` : '0%'}
                                label="Completion Rate"
                                sublabel={stats ? (
                                    stats.completionRate.status === 'no_activity' ? 'No activity yet' :
                                        stats.completionRate.status === 'excellent' ? 'Excellent!' :
                                            stats.completionRate.status === 'good' ? 'Good progress' : 'Keep going!'
                                ) : 'Loading...'}
                                icon={Clock}
                                color={stats?.completionRate.status === 'no_activity' ? (T.textMuted || '#8A8F98') : stats?.completionRate.status === 'excellent' ? T.success : stats?.completionRate.status === 'good' ? T.infoText : T.warningText}
                                bgColor={stats?.completionRate.status === 'no_activity' ? (T.bgHover || '#F2EFED') : stats?.completionRate.status === 'excellent' ? T.successBg : stats?.completionRate.status === 'good' ? T.infoBg : T.warningBg}
                            />
                        </div>
                    )}
                </div>

                {/* ── SECTION 2: ACTION ITEMS | ACTIVITY STREAM | AI INSIGHTS ── */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '20px',
                }}>
                    {/* ── Column 1: Action Items ── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                        {(pendingSurveys.length > 0 || recentAssignments.length > 0) && (
                            <div style={{
                                background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius,
                                padding: T.cardPadding, boxShadow: T.cardShadow,
                            }}>
                                {pendingSurveys.length > 0 && (
                                    <div style={{ marginBottom: recentAssignments.length > 0 ? '24px' : '0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Bot size={18} color={T.purpleText} />
                                                <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: 0 }}>Pending Surveys</h3>
                                            </div>
                                            <button
                                                onClick={() => navigate(`${basePath}/surveys`)}
                                                style={{
                                                    fontSize: '12px', color: T.purpleText, fontWeight: 600, background: 'none', border: 'none',
                                                    display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'
                                                }}
                                            >
                                                View All <ArrowRight size={12} />
                                            </button>
                                        </div>
                                        {pendingSurveys.slice(0, 4).map(survey => (
                                            <InsightCard
                                                key={survey.instanceId}
                                                isPrimary={true}
                                                icon={<Bot size={16} color={T.purpleText} />}
                                                title={survey.title}
                                                subtitle={survey.description}
                                                meta={
                                                    <>
                                                        <span style={{ fontSize: '11px', color: T.textMuted }}>
                                                            Assigned: {new Date(survey.deployedAt).toLocaleDateString()}
                                                        </span>
                                                        {survey.status === 'in_progress' && (
                                                            <span style={{
                                                                fontSize: '10px', fontWeight: 600, color: T.warningText, background: T.warningBg,
                                                                padding: '2px 8px', borderRadius: '12px'
                                                            }}>
                                                                In Progress
                                                            </span>
                                                        )}
                                                    </>
                                                }
                                                action={survey.status === 'in_progress' ? 'Continue' : 'Start'}
                                                onClick={() => startSurvey(survey.instanceId)}
                                            />
                                        ))}
                                    </div>
                                )}

                                {recentAssignments.length > 0 && (
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Sparkles size={18} color={T.infoText} />
                                                <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: 0 }}>Recently Assigned</h3>
                                            </div>
                                            <button
                                                onClick={() => navigate(`${basePath}/tasks`)}
                                                style={{
                                                    fontSize: '12px', color: T.infoText, fontWeight: 600, background: 'none', border: 'none',
                                                    display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'
                                                }}
                                            >
                                                View All <ArrowRight size={12} />
                                            </button>
                                        </div>
                                        {recentAssignments.slice(0, 4).map(assignment => {
                                            const dueInfo = formatDueDate(assignment.due_date);
                                            return (
                                                <InsightCard
                                                    key={assignment.id}
                                                    isPrimary={false}
                                                    icon={getTaskIcon(assignment.task_type)}
                                                    title={assignment.task_title}
                                                    subtitle={assignment.task_description}
                                                    meta={
                                                        <>
                                                            <span style={{
                                                                fontSize: '10px', fontWeight: 600, color: T.infoText, background: T.infoBg,
                                                                padding: '2px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '2px'
                                                            }}>
                                                                <Sparkles size={10} /> NEW
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: T.textSecondary, textTransform: 'capitalize' }}>
                                                                {assignment.task_type}
                                                            </span>
                                                            {dueInfo && (
                                                                <span style={{ fontSize: '11px', fontWeight: 500, color: dueInfo.color }}>
                                                                    {dueInfo.text}
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                    action="View"
                                                    onClick={() => navigate(`${basePath}/tasks`)}
                                                    onDelete={() => dismissNotification(assignment.id)}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Fallback if no pending items */}
                        {pendingSurveys.length === 0 && recentAssignments.length === 0 && !loadingStats && (
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', padding: '40px 16px', color: T.textMuted,
                                textAlign: 'center', border: `1px dashed ${T.border}`,
                                borderRadius: T.cardRadius, background: T.bgPage, height: '100%'
                            }}>
                                <CheckCircle2 size={32} color={T.success} style={{ marginBottom: '12px', opacity: 0.8 }} />
                                <div style={{ fontSize: T.fontBase, fontWeight: 600, color: T.textPrimary }}>You're all caught up!</div>
                                <div style={{ fontSize: T.fontSm, marginTop: '4px' }}>No pending tasks or surveys.</div>
                            </div>
                        )}

                    </div>

                    {/* ── Column 2: Activity Stream ── */}
                    <div style={{
                        background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius,
                        padding: T.cardPadding, boxShadow: T.cardShadow,
                        display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Zap size={18} color={T.accent} />
                                <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: 0 }}>Activity Stream</h3>
                            </div>
                            {loadingActivities && <Loader2 size={16} className="animate-spin" style={{ color: T.textSecondary }} />}
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', minHeight: '320px', maxHeight: '500px' }}>
                            {displayActivities.map((act, i) => (
                                <ActivityItem key={i} {...act} />
                            ))}
                        </div>

                        {activities.length > 0 && (
                            <button
                                onClick={() => navigate(`${basePath}/my-activities`)}
                                style={{
                                    marginTop: '14px', width: '100%', padding: '10px',
                                    background: T.bgHover, color: T.textPrimary, border: 'none',
                                    borderRadius: T.btnRadius, fontSize: T.fontSm, fontWeight: 600,
                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '6px',
                                    transition: 'background 0.2s'
                                }}
                            >
                                View full history <ArrowUpRight size={14} />
                            </button>
                        )}
                    </div>

                    {/* ── Column 3: HR Assistance ── */}
                    <div style={{
                        background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius,
                        padding: T.cardPadding, boxShadow: T.cardShadow,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <Bot size={20} color={T.accent} />
                            <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: 0 }}>Assistant</h3>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ fontSize: T.fontBase, color: T.textSecondary, marginBottom: '8px' }}>
                                Need help? Ask the AI assistant about your tasks, policies, or employment details.
                            </div>

                            <InsightCard
                                isPrimary={false}
                                icon={<MessageSquare size={16} color={T.infoText} />}
                                title="Ask Assistant"
                                subtitle="Get instant answers"
                                meta={<span style={{ fontSize: '11px', color: T.textMuted }}>24/7 Availability</span>}
                                action="Chat"
                                onClick={() => navigate(`${basePath}/ai`)}
                            />

                            <InsightCard
                                isPrimary={false}
                                icon={<FileText size={16} color={T.successText} />}
                                title="Browse Documents"
                                subtitle="Access policies & forms"
                                meta={<span style={{ fontSize: '11px', color: T.textMuted }}>Company Files</span>}
                                action="View"
                                onClick={() => navigate(`${basePath}/files`)}
                            />

                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default DashboardHomeEmployee;
